import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Cake, Eye, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Apiurl from "../environment/Apiurl";

function PatientBirthday() {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const getLocalDateInputValue = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayInput = getLocalDateInputValue();

  // Default: show today's birthdays.
  // User can select a From Date and To Date to see birthdays inside that range.
  const [fromDate, setFromDate] = useState(todayInput);
  const [toDate, setToDate] = useState(todayInput);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  const navigate = useNavigate();

  // Supports the current YYYY-MM-DD value from the patient form and also
  // common DD/MM/YYYY / DD-MM-YYYY values if older records use those formats.
  const getDateParts = (value) => {
    if (!value) return null;

    const rawValue = String(value).trim();

    let match = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }

    match = rawValue.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (match) {
      return {
        year: Number(match[3]),
        month: Number(match[2]),
        day: Number(match[1]),
      };
    }

    const parsed = new Date(rawValue);
    if (!Number.isNaN(parsed.getTime())) {
      return {
        year: parsed.getFullYear(),
        month: parsed.getMonth() + 1,
        day: parsed.getDate(),
      };
    }

    return null;
  };

  const parseInputDate = (value) => {
    if (!value) return null;

    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;

    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);

    if (
      date.getFullYear() !== year ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  };

  const formatDisplayDate = (value) => {
    const date = typeof value === "string" ? parseInputDate(value) : value;
    if (!date || Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateOfBirth = (value) => {
    const parts = getDateParts(value);
    if (!parts) return value || "-";

    return `${String(parts.day).padStart(2, "0")}-${String(
      parts.month
    ).padStart(2, "0")}-${parts.year}`;
  };

  const fromDateObject = useMemo(() => parseInputDate(fromDate), [fromDate]);
  const toDateObject = useMemo(() => parseInputDate(toDate), [toDate]);

  const isInvalidRange =
    fromDateObject && toDateObject && fromDateObject > toDateObject;

  const isTodayRange = fromDate === todayInput && toDate === todayInput;

  const rangeLabel = useMemo(() => {
    if (!fromDateObject || !toDateObject) return "Select date range";

    if (fromDate === toDate) {
      return formatDisplayDate(fromDateObject);
    }

    return `${formatDisplayDate(fromDateObject)} - ${formatDisplayDate(
      toDateObject
    )}`;
  }, [fromDate, toDate, fromDateObject, toDateObject]);

  // Returns the first birthday occurrence that falls inside the selected range.
  // DOB year is ignored for matching; only birthday day + month are used.
  // This also supports ranges that cross New Year.
  const getBirthdayOccurrenceInRange = (patient) => {
    if (!fromDateObject || !toDateObject || isInvalidRange) return null;

    const dobParts = getDateParts(patient.date_of_birth);
    if (!dobParts?.month || !dobParts?.day) return null;

    for (
      let year = fromDateObject.getFullYear();
      year <= toDateObject.getFullYear();
      year += 1
    ) {
      const birthdayDate = new Date(year, dobParts.month - 1, dobParts.day);
      birthdayDate.setHours(0, 0, 0, 0);

      // Prevent invalid dates such as Feb 29 being shifted into March.
      if (
        birthdayDate.getMonth() + 1 !== dobParts.month ||
        birthdayDate.getDate() !== dobParts.day
      ) {
        continue;
      }

      if (
        birthdayDate >= fromDateObject &&
        birthdayDate <= toDateObject
      ) {
        return birthdayDate;
      }
    }

    return null;
  };

  const getBirthdayAge = (patient) => {
    const parts = getDateParts(patient.date_of_birth);
    const birthdayYear = patient.__birthdayOccurrence?.getFullYear();

    if (parts?.year && birthdayYear) {
      return birthdayYear - parts.year;
    }

    return patient.age || "-";
  };

  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const roleId = localStorage.getItem("role_id");

      if (!roleId) {
        setError("No role assigned to the user.");
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      const roleData = Array.isArray(response.data)
        ? response.data.find((role) => role.role_id === roleId)
        : null;

      if (!roleData) {
        setError("No role data found for the current user.");
        return;
      }

      const privileges = {};
      (roleData.pages || []).forEach((page) => {
        privileges[page.page] = {
          access: page.access,
          ...(page.priviallages || page.privileges || {}),
        };
      });

      setUserPrivileges(privileges);
    } catch (err) {
      console.error("Error fetching patient birthday privileges:", err);
      setError("Failed to fetch user privileges.");
    } finally {
      setLoadingPrivileges(false);
    }
  };

  const hasPatientsReadPermission = () => {
    const permission = userPrivileges["Patients"];
    if (!permission) return false;
    return Boolean(permission.access && (permission.all || permission.read));
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError("");

      const userType = localStorage.getItem("user_type") || "unknown";
      let data = [];

      // Keep the same patient visibility rule used in Patients.js.
      if (userType === "Doctor") {
        const username = localStorage.getItem("username") || "";
        if (!username) throw new Error("Doctor username not found");

        const doctorResponse = await axios.get(
          `${Apiurl}/doctor_by_doctor_id/${username}`
        );
        const doctorId = doctorResponse.data?._id;
        if (!doctorId) throw new Error("Doctor ID not found");

        const patientResponse = await axios.get(
          `${Apiurl}/doctor_patients/${doctorId}`
        );
        data = patientResponse.data || [];
      } else {
        const response = await axios.get(`${Apiurl}/patient_details`);
        data = response.data || [];
      }

      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching birthday patients:", err);
      setPatients([]);
      setError("Failed to fetch patient birthday data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPatientsReadPermission()) {
      fetchPatients();
    } else if (!loadingPrivileges) {
      setLoading(false);
    }
  }, [loadingPrivileges, userPrivileges]);

  const birthdayPatients = useMemo(() => {
    if (!fromDateObject || !toDateObject || isInvalidRange) return [];

    return patients
      .map((patient) => {
        const occurrence = getBirthdayOccurrenceInRange(patient);
        if (!occurrence) return null;

        return {
          ...patient,
          __birthdayOccurrence: occurrence,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const dateDiff =
          a.__birthdayOccurrence.getTime() -
          b.__birthdayOccurrence.getTime();

        if (dateDiff !== 0) return dateDiff;

        return `${a.first_name || ""} ${a.last_name || ""}`.localeCompare(
          `${b.first_name || ""} ${b.last_name || ""}`
        );
      });
  }, [patients, fromDateObject, toDateObject, isInvalidRange]);

  const filteredBirthdayPatients = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return birthdayPatients;

    return birthdayPatients.filter((patient) =>
      [
        patient.patient_id,
        patient.first_name,
        patient.last_name,
        patient.patient_type,
        patient.mobile_no,
        patient.gender,
        patient.date_of_birth,
      ].some((value) => String(value || "").toLowerCase().includes(search))
    );
  }, [birthdayPatients, searchTerm]);

  const resetToToday = () => {
    const today = getLocalDateInputValue();
    setFromDate(today);
    setToDate(today);
  };

  if (!loadingPrivileges && !hasPatientsReadPermission()) {
    return (
      <div className="p-3">
        <div className="alert alert-danger mb-0">
          You don't have permission to view patient birthdays.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <style>{`
        .birthday-summary-card {
          border: 0;
          border-radius: 12px;
          background: linear-gradient(135deg, #01AEBE 0%, #248b8a 100%);
          color: #fff;
        }

        .birthday-icon-box {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: rgba(255,255,255,.16);
          flex-shrink: 0;
        }

        .birthday-count {
          min-width: 44px;
          height: 44px;
          padding: 0 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 22px;
          font-size: 20px;
          font-weight: 700;
          background: #fff;
          color: #01AEBE;
        }

        .birthday-table thead {
          background-color: #01AEBE;
          color: #fff;
        }

        .birthday-type-badge {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 20px;
          background: #e8f5f5;
          color: #01AEBE;
          font-size: 12px;
          font-weight: 600;
        }

        .birthday-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #6c757d;
          margin-bottom: 4px;
        }
      `}</style>

      <div className="card birthday-summary-card shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="birthday-icon-box">
              <Cake size={26} />
            </div>

            <div>
              <h5 className="mb-1">Patient Birthday</h5>
              <div className="small opacity-75">
                {isTodayRange ? "Today's Birthdays" : "Birthday Range"} -{" "}
                {rangeLabel}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="small">Total Birthday Patients</span>
            <span className="birthday-count">{birthdayPatients.length}</span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card card-body shadow-sm border-0">
        <div className="row mb-3 align-items-end g-2">
          <div className="col-12 col-lg-4">
            <h6 className="mb-1">
              {isTodayRange
                ? "Today's Birthday Patients"
                : "Birthday Patients"}
            </h6>
            <small className="text-muted">
              {isTodayRange
                ? "Patients whose birthday is today."
                : `Patients whose birthday falls between ${rangeLabel}.`}
            </small>
          </div>

          <div className="col-6 col-md-3 col-lg-2">
            <label className="birthday-filter-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          <div className="col-6 col-md-3 col-lg-2">
            <label className="birthday-filter-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={toDate}
              min={fromDate || undefined}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>

          <div className="col-12 col-md-2 col-lg-1">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={resetToToday}
            >
              Today
            </button>
          </div>

          <div className="col-12 col-md-4 col-lg-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search patient"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />

              <Search
                size={16}
                className="position-absolute text-muted"
                style={{
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </div>
          </div>
        </div>

        {isInvalidRange && (
          <div className="alert alert-warning py-2">
            To Date must be the same as or later than From Date.
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover birthday-table mb-0">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Patient ID</th>
                <th>Patient Name</th>
                <th>Type</th>
                <th>Date of Birth</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Mobile No</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    Loading birthday patients...
                  </td>
                </tr>
              ) : isInvalidRange ? (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-muted">
                    Please select a valid From Date and To Date.
                  </td>
                </tr>
              ) : filteredBirthdayPatients.length > 0 ? (
                filteredBirthdayPatients.map((patient, index) => (
                  <tr
                    key={patient._id || patient.patient_id}
                    className="align-middle"
                  >
                    <td>{index + 1}</td>
                    <td>{patient.patient_id || "-"}</td>

                    <td>
                      {[patient.first_name, patient.last_name]
                        .filter(Boolean)
                        .join(" ") || "-"}
                    </td>

                    <td>
                      <span className="birthday-type-badge">
                        {patient.patient_type || "-"}
                      </span>
                    </td>

                    <td>{formatDateOfBirth(patient.date_of_birth)}</td>
                    <td>{getBirthdayAge(patient)}</td>
                    <td>{patient.gender || "-"}</td>
                    <td>{patient.mobile_no || "-"}</td>

                    <td>
                      <button
                        type="button"
                        className="btn btn-sm p-1 border-0"
                        title="View Patient"
                        onClick={() =>
                          navigate(`/patients/view/${patient.patient_id}`, {
                            state: { patient },
                          })
                        }
                      >
                        <Eye size={17} className="text-secondary" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-muted">
                    <Cake size={30} className="mb-2" />

                    <div>
                      {searchTerm
                        ? "No birthday patients match your search in the selected date range."
                        : `No patient birthdays between ${rangeLabel}.`}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PatientBirthday;